import { useEffect, useState } from 'react'
import type {
  AircraftSummary,
  PendingArrival,
  ScoreState,
  ShiftState,
  WindState,
} from '../state/store'
import { WindIndicator } from './WindIndicator'

const TOAST_LIFETIME_MS = 3000

/// Compact score readout in the chrome. Top-left of the radar shows
/// the running total + the success/total handled count; recent score
/// events flash beneath as toasts that fade after ~3 seconds.
///
/// When an aircraft is selected, a one-line "→ TARGET · 12.3 NM ·
/// 03:42" row sits between the score and the events list,
/// surfacing where the picked aircraft is going and when it
/// arrives. Falls back to "—" for ETA when the aircraft is heading
/// away from its target (so the player sees they need to vector).

export function ScoreHud(props: {
  score: ScoreState
  selected: AircraftSummary | null
  wind: WindState
  shift?: ShiftState
  isLive?: boolean
  pendingArrivals: PendingArrival[]
  onPickArrival: (id: string) => void
}) {
  const { score, selected, wind, shift, isLive, pendingArrivals, onPickArrival } = props
  // Tick a counter so the visible-toast filter re-runs even when
  // `score` itself hasn't changed (the in-flight toasts age out).
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 250)
    return () => window.clearInterval(id)
  }, [])

  const now = performance.now()
  const visible = score.recent
    .filter((e) => now - e.timestamp < TOAST_LIFETIME_MS)
    .slice(0, 4)

  return (
    // Sits on the LEFT of the screen so it never crashes into the
    // top-right toolbar. Phone bumps the top offset down (top-12)
    // matching the toolbar height; desktop keeps the same offset
    // since the toolbar is a similar size.
    <div className="pointer-events-none absolute left-2 top-11 z-10 select-none md:left-4 md:top-12">
      <div className="rounded-md bg-slate-900/70 px-2 py-1 text-xs font-semibold text-white/90 shadow ring-1 ring-white/10 md:px-3 md:py-1.5 md:text-sm">
        Score <span className="ml-1 font-mono">{score.total.toLocaleString()}</span>
        {score.expectedTotal > 0 && (
          <span className="ml-2 text-[10px] font-normal text-white/50 md:text-xs">
            {score.successCount}/{score.expectedTotal}
            {score.targetCompleted > 0 && (
              <span
                className={
                  'ml-2 ' +
                  (score.successCount >= score.targetCompleted
                    ? 'text-emerald-300'
                    : 'text-amber-300/80')
                }
              >
                need {score.targetCompleted}
              </span>
            )}
          </span>
        )}
      </div>
      {isLive && shift && shift.goal > 0 && (
        <div className="mt-1.5 rounded-md bg-slate-900/70 px-2 py-1 text-xs font-semibold text-white/90 shadow ring-1 ring-white/10 md:mt-2 md:px-3 md:py-1.5 md:text-sm">
          Worked{' '}
          <span
            className={
              'font-mono ' +
              (shift.aircraftWorked >= shift.goal ? 'text-emerald-300' : 'text-sky-300')
            }
          >
            {shift.aircraftWorked}/{shift.goal}
          </span>
        </div>
      )}
      {wind.speedKts > 0 && (
        <div className="mt-1.5 md:mt-2">
          <WindIndicator wind={wind} />
        </div>
      )}
      {pendingArrivals.length > 0 && (
        <ul className="mt-1.5 space-y-1 md:mt-2">
          {pendingArrivals.slice(0, 4).map((p) => (
            <li key={p.id}>
              {/* pointer-events-auto: the HUD wrapper is
                  pointer-events-none; these rows are tappable —
                  click = select the aircraft (which dismisses the
                  row). Player feedback (dev board, Dandop,
                  2026-06-11): arrival toasts faded in 3 s and were
                  easy to miss; these persist until handled. */}
              <button
                onClick={() => onPickArrival(p.id)}
                className={
                  'pointer-events-auto flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] font-medium shadow ring-1 ring-white/10 md:text-xs ' +
                  (p.isEmergency
                    ? 'bg-rose-950/80 text-rose-200 hover:bg-rose-900/80'
                    : 'bg-slate-900/70 text-amber-200/90 hover:bg-slate-800/80')
                }
              >
                <span>{p.isEmergency ? '⚠' : '🛬'}</span>
                <span className="font-semibold text-white/90">{p.flightNumber}</span>
                <span className="truncate text-white/60">{p.detail}</span>
              </button>
            </li>
          ))}
          {pendingArrivals.length > 4 && (
            <li className="px-2 text-[10px] text-white/50">
              +{pendingArrivals.length - 4} more
            </li>
          )}
        </ul>
      )}
      {selected && selected.targetName && (
        <div className="mt-1.5 rounded-md bg-slate-900/70 px-2 py-1 text-[11px] font-medium text-white/80 shadow ring-1 ring-white/10 md:mt-2 md:text-xs">
          <span className="text-slate-400">{selected.flightNumber} →</span>
          <span className="ml-1 font-semibold">{selected.targetName}</span>
          {selected.distanceToTargetNM != null && (
            <span className="ml-1.5 font-mono text-slate-300">
              {selected.distanceToTargetNM.toFixed(1)} NM
            </span>
          )}
          <span className="ml-1.5 font-mono text-slate-300">
            {formatEta(selected.etaSec)}
          </span>
        </div>
      )}
      <ul className="mt-1.5 space-y-1 text-[11px] md:mt-2 md:text-xs">
        {visible.map((e) => (
          <li
            key={e.id}
            className={
              'rounded bg-slate-900/60 px-2 py-1 font-mono shadow ' +
              (e.points >= 0 ? 'text-emerald-300' : 'text-rose-300')
            }
          >
            {e.points >= 0 ? '+' : ''}
            {e.points} <span className="ml-1 text-slate-300/80">{e.description}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/// "MM:SS" or "HH:MM" if over an hour. "—" when ETA is null
/// (heading away from target / on the ground / stationary).
function formatEta(etaSec: number | null): string {
  if (etaSec == null || !Number.isFinite(etaSec)) return '—'
  const total = Math.max(0, Math.round(etaSec))
  if (total >= 3600) {
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    return `${h}:${String(m).padStart(2, '0')}`
  }
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
